type AnalyticsValue = string | number | boolean;

const BLOCKED_PARAMETER_KEYS = new Set([
  'email',
  'event_name',
  'eventname',
  'first_name',
  'firstname',
  'guest_name',
  'guestname',
  'event_question_answers',
  'event_questions',
  'guestquestionanswers',
  'lastname',
  'last_name',
  'message',
  'phone',
  'phone_number',
  'survey_answers',
  'user_agent',
]);

const containsSensitiveContent = (key: string) => {
  const compactKey = key.replace(/_/g, '');
  return ['email', 'phone', 'guestname', 'firstname', 'lastname', 'message', 'answers']
    .some((sensitiveKey) => compactKey.includes(sensitiveKey));
};

const normalizeName = (name: string) => name
  .trim()
  .toLowerCase()
  .replace(/[^a-z0-9_]+/g, '_')
  .replace(/^_+|_+$/g, '')
  .slice(0, 40);

const sanitizeString = (key: string, value: string) => {
  if (/(url|href|path|action)$/.test(key)) {
    try {
      return new URL(value, window.location.origin).pathname.slice(0, 100);
    } catch {
      return value.split('?')[0].slice(0, 100);
    }
  }

  return value.slice(0, 100);
};

const getSafeParameters = (properties?: Record<string, unknown>) => {
  const safeParameters: Record<string, AnalyticsValue> = {};

  Object.entries(properties || {}).slice(0, 25).forEach(([rawKey, rawValue]) => {
    const key = normalizeName(rawKey);
    if (!key || BLOCKED_PARAMETER_KEYS.has(key) || containsSensitiveContent(key) || rawValue == null) return;

    if (typeof rawValue === 'string') {
      safeParameters[key] = sanitizeString(key, rawValue);
    } else if (typeof rawValue === 'number' && Number.isFinite(rawValue)) {
      safeParameters[key] = rawValue;
    } else if (typeof rawValue === 'boolean') {
      safeParameters[key] = rawValue;
    } else if (rawValue instanceof Date) {
      safeParameters[key] = rawValue.toISOString();
    }
  });

  return safeParameters;
};

export function trackGoogleAnalyticsEvent(
  eventName: string,
  properties?: Record<string, unknown>,
) {
  if (typeof window === 'undefined') return;

  try {
    const normalizedEventName = normalizeName(eventName);
    if (!normalizedEventName) return;

    const analyticsWindow = window as Window & {
      dataLayer?: unknown[];
      gtag?: (...args: unknown[]) => void;
    };

    analyticsWindow.dataLayer = analyticsWindow.dataLayer || [];
    analyticsWindow.gtag = analyticsWindow.gtag || function gtag(..._args: unknown[]) {
      analyticsWindow.dataLayer?.push(arguments);
    };

    analyticsWindow.gtag('event', normalizedEventName, getSafeParameters(properties));
  } catch (error) {
    console.warn('Failed to track Google Analytics event:', eventName, error);
  }
}
